import { useState, useEffect, useRef, useCallback } from "react";
import { Search, Download, X, ChevronDown, ArrowRight } from "lucide-react";
import html2canvas from "html2canvas";
import axios from "axios";
import NavBar from "@/components/NavBar";
import { useToast } from "@/hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SelectField = ({ label, value, onChange, options, placeholder, testId }) => (
  <div>
    <label className="text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-1 block">
      {label}
    </label>
    <div className="relative">
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-12 bg-neutral-900 border border-neutral-800 rounded-md px-4 pr-10 text-neutral-50 focus:border-sky-400 focus:outline-none appearance-none text-sm"
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>{o.name}</option>
        ))}
      </select>
      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500 pointer-events-none" />
    </div>
  </div>
);

const TrasladosPage = () => {
  const { toast } = useToast();
  const [sedes, setSedes] = useState([]);
  const [zonas, setZonas] = useState([]);
  const [products, setProducts] = useState([]);
  const [sedeOrigen, setSedeOrigen] = useState("");
  const [sedeDestino, setSedeDestino] = useState("");
  const [selectedZona, setSelectedZona] = useState("");
  const [selectedFecha, setSelectedFecha] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [quantities, setQuantities] = useState({});
  const [isExporting, setIsExporting] = useState(false);
  const [filterByZona, setFilterByZona] = useState(false);
  const ticketRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sedesRes, zonasRes, productsRes] = await Promise.all([
          axios.get(`${API}/sedes`, { params: { skip: 0, limit: 1000 } }),
          axios.get(`${API}/zonas`, { params: { skip: 0, limit: 1000 } }),
          axios.get(`${API}/productos/pedidos`, { params: { skip: 0, limit: 1000 } }),
        ]);
        const sedes = sedesRes.data.items ?? sedesRes.data;
        const zonas = zonasRes.data.items ?? zonasRes.data;
        setSedes(sedes);
        setZonas(zonas);
        setProducts(productsRes.data.items ?? productsRes.data);
        if (sedes.length > 0) setSedeOrigen(sedes[0].id);
        if (sedes.length > 1) setSedeDestino(sedes[1].id);
        if (zonas.length > 0) setSelectedZona(zonas[0].id);
      } catch (err) {
        console.error("Error cargando datos:", err);
        toast({
          title: "Error al cargar datos",
          description: err.response?.status === 500
            ? "Error del servidor. Intenta más tarde."
            : "No se pudieron cargar los datos. Verifica tu conexión.",
          variant: "destructive",
        });
      }
    };
    fetchData();
  }, []);

  const updateQuantity = useCallback((productId, field, value) => {
    const normalizedValue = value === "" ? "" : value.replace(/[^0-9]/g, "");
    setQuantities((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [field]: normalizedValue === "" ? "" : Number(normalizedValue),
      },
    }));
  }, []);

  // Filtro por zona (activo solo cuando filterByZona está activado)
  const zonaProducts = !filterByZona
    ? products
    : selectedZona
    ? products.filter((p) => p.zona_id === selectedZona)
    : [];

  const filteredProducts = zonaProducts.filter((p) =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Exportar: productos donde enviada > 0
  const exportProducts = zonaProducts.filter(
    (p) => Number(quantities[p.id]?.enviada || 0) > 0
  );

  const origenName = sedes.find((s) => s.id === sedeOrigen)?.name || "—";
  const destinoName = sedes.find((s) => s.id === sedeDestino)?.name || "—";
  const zonaName = zonas.find((z) => z.id === selectedZona)?.name || "—";
  const formattedDate = selectedFecha
    ? new Date(selectedFecha + "T12:00:00").toLocaleDateString("es-CO", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "—";

  const exportTicket = async () => {
    if (exportProducts.length === 0) return;
    setIsExporting(true);
    await new Promise((r) => setTimeout(r, 150));
    try {
      const canvas = await html2canvas(ticketRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const link = document.createElement("a");
      const filename = `traslado_${selectedFecha || "fecha"}_${origenName}-${destinoName}.png`
        .replace(/\s+/g, "_");
      link.download = filename;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (err) {
      console.error("Error generando ticket:", err);
      toast({
        title: "Error al exportar",
        description: "No se pudo generar el ticket. Intenta nuevamente.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div
      className="bg-neutral-950 flex flex-col max-w-md mx-auto relative"
      style={{ fontFamily: "'Outfit', sans-serif", minHeight: "100dvh" }}
    >
      <NavBar title="Traslados" subtitle="Registro de traslado" />

      {/* Header form */}
      <div className="px-4 pt-4 pb-3 space-y-3 border-b border-neutral-800 bg-neutral-950">
        {/* Origen → Destino */}
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <SelectField
              label="Sede origen"
              value={sedeOrigen}
              onChange={setSedeOrigen}
              options={sedes}
              placeholder="— Origen —"
              testId="select-origen"
            />
          </div>
          <div className="pb-3 shrink-0">
            <ArrowRight className="w-5 h-5 text-sky-400" />
          </div>
          <div className="flex-1">
            <SelectField
              label="Sede destino"
              value={sedeDestino}
              onChange={setSedeDestino}
              options={sedes}
              placeholder="— Destino —"
              testId="select-destino"
            />
          </div>
        </div>

        {/* Fecha + Zona */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-1 block">
              Fecha
            </label>
            <input
              data-testid="input-fecha"
              type="date"
              value={selectedFecha}
              onChange={(e) => setSelectedFecha(e.target.value)}
              className="w-full h-12 bg-neutral-900 border border-neutral-800 rounded-md px-3 text-neutral-50 focus:border-sky-400 focus:outline-none text-sm"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold tracking-widest uppercase text-neutral-500">
                Zona
              </label>
              <button
                data-testid="traslados-toggle-filter-zona"
                onClick={() => setFilterByZona((v) => !v)}
                className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
                  filterByZona
                    ? "bg-sky-400/15 text-sky-400"
                    : "bg-neutral-800 text-neutral-500"
                }`}
              >
                <span
                  className={`w-2 h-2 rounded-full inline-block transition-colors ${
                    filterByZona ? "bg-sky-400" : "bg-neutral-600"
                  }`}
                />
                Filtrar
              </button>
            </div>
            <div className="relative">
              <select
                data-testid="select-zona"
                value={selectedZona}
                onChange={(e) => setSelectedZona(e.target.value)}
                className="w-full h-12 bg-neutral-900 border border-neutral-800 rounded-md px-4 pr-10 text-neutral-50 focus:border-sky-400 focus:outline-none appearance-none text-sm"
              >
                <option value="">— Zona —</option>
                {zonas.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500 pointer-events-none" />
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="px-4 py-3 border-b border-neutral-800 bg-neutral-950">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
          <input
            data-testid="input-search-product"
            type="text"
            placeholder="Buscar producto..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-11 bg-neutral-900 border border-neutral-800 rounded-md pl-9 pr-9 text-neutral-50 placeholder:text-neutral-600 focus:border-sky-400 focus:outline-none text-sm"
          />
          {searchQuery && (
            <button
              data-testid="btn-clear-search"
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <X className="w-4 h-4 text-neutral-500" />
            </button>
          )}
        </div>
      </div>

      {/* Column headers */}
      <div className="px-4 py-2 flex items-center bg-neutral-950 border-b border-neutral-800">
        <div className="flex-1 text-xs font-semibold tracking-widest uppercase text-neutral-500">
          Producto
        </div>
        <div className="w-14 text-center text-xs font-semibold tracking-widest uppercase text-neutral-500">
          Pedido
        </div>
        <div className="w-14 text-center text-xs font-semibold tracking-widest uppercase text-neutral-500 ml-2">
          Enviada
        </div>
      </div>

      {/* Product list */}
      <div className="flex-1 overflow-y-auto pb-28">
        {products.length === 0 ? (
          <div className="px-4 py-14 text-center">
            <p className="text-neutral-400 text-sm">Sin productos configurados.</p>
            <p className="text-neutral-600 text-xs mt-1">
              Ve a Administración para agregar productos de pedidos.
            </p>
          </div>
        ) : filterByZona && !selectedZona ? (
          <div className="px-4 py-14 text-center">
            <p className="text-neutral-400 text-sm">Selecciona una zona</p>
            <p className="text-neutral-600 text-xs mt-1">
              Elige una zona para ver sus productos.
            </p>
          </div>
        ) : filterByZona && zonaProducts.length === 0 ? (
          <div className="px-4 py-14 text-center">
            <p className="text-neutral-400 text-sm">Sin productos para esta zona.</p>
            <p className="text-neutral-600 text-xs mt-1">
              Agrega productos asignados a esta zona en Administración.
            </p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="px-4 py-10 text-center text-neutral-500 text-sm">
            No se encontraron productos con ese nombre.
          </div>
        ) : (
          filteredProducts.map((product) => {
            const enviada = Number(quantities[product.id]?.enviada || 0);
            const hasEnviada = enviada > 0;
            return (
              <div
                key={product.id}
                data-testid={`product-row-${product.id}`}
                className={`flex items-center px-4 py-3 border-b border-neutral-800 transition-colors duration-150 ${
                  hasEnviada ? "bg-sky-950/30" : ""
                }`}
              >
                <div className="flex-1 min-w-0 pr-2">
                  <div
                    className={`text-sm font-medium truncate ${
                      hasEnviada ? "text-sky-400" : "text-neutral-50"
                    }`}
                  >
                    {product.name}
                  </div>
                </div>
                <input
                  data-testid={`input-pedida-${product.id}`}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  enterKeyHint="next"
                  value={quantities[product.id]?.pedida ?? ""}
                  onChange={(e) =>
                    updateQuantity(product.id, "pedida", e.target.value)
                  }
                  placeholder="0"
                  className="w-14 h-11 bg-neutral-800 border border-neutral-700 rounded text-center text-neutral-50 text-sm font-mono-data focus:border-neutral-600 focus:outline-none"
                />
                <div className="w-2" />
                <input
                  data-testid={`input-enviada-${product.id}`}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  enterKeyHint="next"
                  value={quantities[product.id]?.enviada ?? ""}
                  onChange={(e) =>
                    updateQuantity(product.id, "enviada", e.target.value)
                  }
                  placeholder="0"
                  className={`w-14 h-11 border rounded text-center text-sm font-mono-data focus:outline-none transition-colors duration-150 ${
                    hasEnviada
                      ? "bg-sky-950 border-sky-400/60 text-sky-400 focus:border-sky-400"
                      : "bg-neutral-800 border-neutral-700 text-neutral-50 focus:border-neutral-600"
                  }`}
                />
              </div>
            );
          })
        )}
      </div>

      {/* Export footer */}
      <div
        className="fixed bottom-0 left-0 right-0 px-4 pt-3 bg-neutral-950/95 backdrop-blur border-t border-neutral-800 max-w-md mx-auto w-full z-40"
        style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}
      >
        <button
          data-testid="export-traslado-button"
          onClick={exportTicket}
          disabled={exportProducts.length === 0 || isExporting}
          className={`w-full h-14 rounded-md font-semibold text-base flex items-center justify-center gap-2 transition-colors duration-150 ${
            exportProducts.length > 0 && !isExporting
              ? "bg-sky-400 text-neutral-950 active:bg-sky-300"
              : "bg-neutral-800 text-neutral-600 cursor-not-allowed"
          }`}
        >
          <Download className="w-5 h-5" />
          {isExporting
            ? "Generando imagen..."
            : exportProducts.length > 0
            ? `Exportar traslado (${exportProducts.length} producto${exportProducts.length !== 1 ? "s" : ""})`
            : "Ingresa cantidades enviadas"}
        </button>
      </div>

      {/* Hidden ticket para html2canvas — diseño distinto al ticket de pedidos */}
      <div
        ref={ticketRef}
        style={{
          position: "absolute",
          top: "-9999px",
          left: "-9999px",
          backgroundColor: "#ffffff",
          color: "#000000",
          width: "420px",
          fontFamily: "'Courier New', Courier, monospace",
          fontSize: "12px",
          lineHeight: "1.6",
          overflow: "hidden",
        }}
      >
        {/* Barra superior oscura */}
        <div
          style={{
            backgroundColor: "#0c1929",
            color: "#ffffff",
            padding: "14px 18px 12px",
          }}
        >
          <div
            style={{
              fontSize: "9px",
              letterSpacing: "3px",
              opacity: 0.55,
              marginBottom: "4px",
              textTransform: "uppercase",
            }}
          >
            Documento de traslado
          </div>
          <div style={{ fontSize: "17px", fontWeight: "bold", letterSpacing: "0.5px" }}>
            TRASLADO DE PRODUCTOS
          </div>
        </div>

        {/* Origen → Destino */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            backgroundColor: "#e8f4ff",
            padding: "10px 18px",
            gap: "8px",
            borderBottom: "1px solid #b3d4f0",
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: "9px",
                letterSpacing: "2px",
                color: "#555",
                marginBottom: "2px",
                textTransform: "uppercase",
              }}
            >
              Origen
            </div>
            <div style={{ fontWeight: "bold", fontSize: "13px" }}>{origenName}</div>
          </div>
          <div style={{ fontSize: "20px", color: "#0369a1", fontWeight: "bold", padding: "0 4px" }}>
            →
          </div>
          <div style={{ flex: 1, textAlign: "right" }}>
            <div
              style={{
                fontSize: "9px",
                letterSpacing: "2px",
                color: "#555",
                marginBottom: "2px",
                textTransform: "uppercase",
              }}
            >
              Destino
            </div>
            <div style={{ fontWeight: "bold", fontSize: "13px" }}>{destinoName}</div>
          </div>
        </div>

        {/* Meta info: fecha */}
        <div
          style={{
            padding: "8px 18px",
            borderBottom: "2px solid #000000",
            fontSize: "11px",
          }}
        >
          <span style={{ fontWeight: "bold" }}>Fecha: </span>
          <span>{formattedDate}</span>
        </div>

        {/* Cabeceras de columnas */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 62px 62px",
            fontWeight: "bold",
            fontSize: "10px",
            letterSpacing: "1px",
            padding: "6px 18px",
            borderBottom: "1px solid #000000",
            backgroundColor: "#f0f4f8",
            gap: "4px",
            textTransform: "uppercase",
          }}
        >
          <div>Producto</div>
          <div style={{ textAlign: "center" }}>Pedido</div>
          <div style={{ textAlign: "center" }}>Enviada</div>
        </div>

        {/* Filas de productos */}
        {exportProducts.map((p, i) => {
          const pedida = Number(quantities[p.id]?.pedida || 0);
          const enviada = Number(quantities[p.id]?.enviada || 0);
          const diff = enviada - pedida;
          return (
            <div
              key={p.id}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 62px 62px",
                padding: "5px 18px",
                borderBottom: "1px solid #e0e0e0",
                fontSize: "12px",
                backgroundColor: i % 2 === 0 ? "#fafafa" : "#ffffff",
                gap: "4px",
              }}
            >
              <div style={{ paddingLeft: "2px" }}>{p.name}</div>
              <div style={{ textAlign: "center", color: "#666" }}>
                {pedida > 0 ? pedida : "—"}
              </div>
              <div
                style={{
                  textAlign: "center",
                  fontWeight: "bold",
                  color: pedida > 0 && diff < 0 ? "#b91c1c" : "#065f46",
                }}
              >
                {enviada}
              </div>
            </div>
          );
        })}

        {/* Pie del ticket */}
        <div
          style={{
            padding: "10px 18px 16px",
            borderTop: "2px solid #000000",
            fontSize: "11px",
          }}
        >
          <span style={{ fontWeight: "bold" }}>
            Total: {exportProducts.length} producto{exportProducts.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>
    </div>
  );
};

export default TrasladosPage;
